import { cn } from '@/utils/tailwind.util';

/**
 * 배너 이미지가 없을 때의 기본 배경. 세로 그라데이션이다.
 *
 * 스톱이 6개인 이유는 곡선이 선형이 아니기 때문이다 — 양 끝만 잡으면 중간 톤이 달라진다.
 *
 * ⚠️ `#259EFF` 는 ZUS 토큰이 아니다. 디자이너 확인이 필요하다.
 */
const DEFAULT_BANNER_BACKGROUND = ['linear-gradient(180deg', '#259EFF 0%', '#259EFF 20%', '#41AAFF 40%', '#80C6FF 60%', '#D2EBFF 80%', '#FFFFFF 100%)'].join(', ');
export interface DetailHeroBannerProps {
  /** 배너 이미지 URL. 없을 수 있다(맵 템플릿이 없는 퀴즈, 폴더). 그때는 기본 그라데이션 배경을 그린다. */
  bannerUrl?: string;
  /**
   * 이미지의 alt 텍스트.
   * 장식용 배너라면 빈 문자열을 넘긴다 — 접근성 트리에서 빠진다.
   */
  alt?: string;
  /**
   * 이미지가 없을 때 기본 그라데이션 대신 쓸 배경(CSS `background` 값).
   * 폴더 상세가 폴더별 색을 넘긴다. 이미지가 있으면 무시된다.
   */
  background?: string;
  /** 추가 스타일링을 위한 선택적 className */
  className?: string;
}

/**
 * 상세 화면 상단의 전체 너비 배너. 퀴즈 상세와 폴더 상세가 함께 쓴다.
 *
 * 배너 아래로 히어로 카드가 약 60px 겹쳐 올라오고, 겹치는 구간의 가독성을 하단 흰색 그라데이션이 받친다.
 *
 * @example
 * ```tsx
 * <DetailHeroBanner bannerUrl={quizData.mapTemplateBannerImage} alt={templateName} />
 * <DetailHeroBanner />
 * ```
 */
export const DetailHeroBanner = ({
  bannerUrl,
  alt = '',
  background = DEFAULT_BANNER_BACKGROUND,
  className
}: DetailHeroBannerProps) => {
  const frameClassName = cn('relative h-[86px] w-full overflow-hidden tablet-sm:h-[136px]', className);

  // 이미지가 없으면 배경만 그린다. 가독성 오버레이도 두지 않는다 — 배경이 이미 흰색으로 끝난다.
  if (!bannerUrl) {
    return <div className={frameClassName} style={{
      background
    }} aria-hidden />;
  }
  return <div className={frameClassName} data-sentry-component="DetailHeroBanner" data-sentry-source-file="DetailHeroBanner.tsx">
      <img src={bannerUrl} alt={alt}
    // alt 가 빈 문자열이면 장식용이라는 뜻이라 접근성 트리에서 뺀다.
    aria-hidden={alt === '' || undefined} className={'h-full w-full object-cover object-center'} />
      {/* 겹치는 콘텐츠의 텍스트 가독성 향상을 위한 그라데이션 오버레이 */}
      <div className={'absolute inset-x-0 bottom-0 h-20'} style={{
      background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.4) 100%)'
    }} />
    </div>;
};